import { LineString, MultiPolygon, Polygon, Point } from 'ol/geom';

/**
 * Between the MapElements coming from the API and the OpenLayers geometries,
 * we have an isomorphism (they can be converted back and forth without any data loss).
 *
 * For this there is a need for a pair of functions, that can convert between them.
 */

/**
 * Convert MapElement to OpenLayers' Polygon or MultiPolygon
 *
 * @param {MapElement} element the MapElement object (has to have geom field)
 * @return {Polygon | MultiPolygon} the OpenLayers Polygon or MultiPolygon instance
 * @throws Error if the passed shape is not in a convertible format
 */
export const toPolygon = element => {
  const {
    geom,
    ...properties
  } = element;
  switch (geom.type) {
    case 'Polygon':
      {
        const shape = new Polygon(geom.coordinates);
        shape.setProperties(properties);
        return shape;
      }
    case 'MultiPolygon':
      {
        const shape = new MultiPolygon(geom.coordinates);
        shape.setProperties(properties);
        return shape;
      }
    case 'LineString':
      {
        const shape = new LineString(geom.coordinates);
        shape.setProperties(properties);
        return shape;
      }
    case 'Point':
      {
        const shape = new Point(geom.coordinates, properties.size);
        shape.setProperties(properties);
        return shape;
      }
    default:
      throw new Error('Cannot convert shape to OpenLayers polygon.');
  }
};

/**
 * Convert OpenLayers' Polygon or MultiPolygon instances to POJO MapElements object
 * (it reconstructs the original object's properties).
 *
 * @param {Polygon | MultiPolygon} shape the OpenLayers Polygon or MultiPolygon instance
 * @return {MapElement} the MapElement object (has geom field for geometry and other fields from the original metadata)
 */
export const toElement = shape => {
  if (shape instanceof MultiPolygon) {
    return {
      ...shape.getProperties(),
      geom: {
        type: 'MultiPolygon',
        coordinates: shape.getCoordinates()
      }
    };
  }
  if (shape instanceof Polygon) {
    return {
      ...shape.getProperties(),
      geom: {
        type: 'Polygon',
        coordinates: shape.getCoordinates()
      }
    };
  }
  if (shape instanceof LineString) {
    return {
      ...shape.getProperties(),
      geom: {
        type: 'LineString',
        coordinates: shape.getCoordinates()
      }
    };
  }
  if (shape instanceof Point) {
    return {
      ...shape.getProperties(),
      geom: {
        type: 'Point',
        coordinates: shape.getCoordinates()
      }
    };
  }
  throw new Error('Cannot convert to MapElement.');
};